import { useEffect, useMemo, useState } from 'react';
import NoImg from 'assets/img/no-img.jpg';
import api from 'services/api';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import clsx from 'clsx';
import { CloseIcon } from 'components/icons/CloseIcon';
import { useLocation } from 'react-router-dom';
import { LinkIcon } from 'components/icons/LinkIcon';
import { Switch } from 'components/Switch';
import clipboard from 'clipboard-copy';
import useActiveWorkspace from 'hooks/useActiveWorkspace';
import { fetchVideoIsShared } from 'api/videosAPI';
import { duration } from 'moment';
import { isNil } from 'lodash';

import { getLastVideoByChannel } from 'store/secondaryChannelVideoSlice';

import { FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import useVideo from 'hooks/useVideo';

interface ExternalSharingProps {
  videoId: string;
  videoName: string;
  initChannel?: string;
  videoHashId: string;
  sharingOption: string;
  setVideoSharing: (value: string) => void;
  onCancel: () => void;
  videoShots?: any[];
}

export default function ExternalSharing({
  videoId,
  videoHashId,
  videoName,
  initChannel,
  sharingOption,
  setVideoSharing,
  onCancel,
  videoShots
}: ExternalSharingProps) {
  const [selectedChannel, handleSelectedChannel] = useState<null & any>(initChannel ? initChannel : null);
  const [sharedChannels, handleSharedChannels] = useState([] as any[]);
  const channelList = useSelector((state: RootState) => state.channels.channelList);
  const userData = useSelector((state: RootState) => state.user.user);
  const location = useLocation();
  const channelIdRegex = /\/channel\/([^/]+)/;
  const channelMatch = location.pathname.match(channelIdRegex);
  const channelId = channelMatch ? channelMatch[1] : 'home';

  const [selectedVideo, setSelectedVideo] = useState(null as any);
  const [enableExternal, setEnableExternal] = useState(null as any);
  const [copied, setCopied] = useState(false);
  const [ssoCopied, setSSOCopied] = useState(false);
  const [ssoDownloadCopied, setDownloadCopied] = useState(false);
  const { activeWorkspace } = useActiveWorkspace();
  const [timestamp, setTimestamp] = useState<number | null>(null);
  const [startAt, setStartAt] = useState<boolean>(false);
  const [shotId, setShotId] = useState<string | null>(null);

  const secondaryViewVideos = useSelector((state: RootState) => state.secondaryChannelVideos);
  useEffect(() => {
    const getVideo = getLastVideoByChannel(secondaryViewVideos, channelId);
    // const getVideo = getLastVideoByChannel(secondaryViewVideos, channelId);
    const playerRef = secondaryViewVideos.channels[channelId].playerRef;

    if (playerRef) {
      const time = playerRef?.getCurrentTime();
      setTimestamp(time);
    }

    if (videoShots && getVideo) {
      const videoShotHashId = videoShots[getVideo?.selectedShot || 0]?.shot_hash_id;
      if (videoShotHashId) {
        setShotId(videoShotHashId);
      }
    }
  }, []);

  const { video, selectVideo, loading } = useVideo(videoHashId, channelId, videoHashId);

  useEffect(() => {
    if (selectVideo) {
      setSelectedVideo(selectVideo);
    } else {
      setSelectedVideo(video);
    }
  }, [loading]);

  const formattedTime = useMemo(() => {
    if (!isNil(timestamp) && timestamp > 3600) {
      return duration(timestamp, 'seconds').format('h:mm:ss', { trim: false });
    } else {
      return duration(timestamp, 'seconds').format('m:ss', { trim: false });
    }
  }, [timestamp]);

  useEffect(() => {
    const fetchData = async () => {
      if (!videoHashId) return;
      try {
        const data = await fetchVideoIsShared(videoHashId);
        setEnableExternal(data);
      } catch (error) {
        setEnableExternal(false);
      }
    };
    fetchData();
  }, []);

  const copyLink = async () => {
    api.get(`api/videos/${videoHashId}/external-link/`).then((res) => {
      clipboard(res.data.external_link);
      setCopied(true);
    });
  };

  const copySSOLink = async () => {
    const url = startAt
      ? `api/videos/${videoHashId}/share-replay-link/?timestamp=${timestamp}`
      : `api/videos/${videoHashId}/share-replay-link/`;
    api.get(url).then((res) => {
      clipboard(res.data.share_link);
      setSSOCopied(true);
    });
  };

  const copyDownloadSSOLink = () => {
    const url = `/api/videos/${videoHashId}/shots/${shotId}/share/download-link/`;
    api.get(url).then((res) => {
      clipboard(res.data.download_link);
      setDownloadCopied(true);
    });
  };

  useEffect(() => {
    if (videoId) {
      api.get(`api/videos/${videoId}/shares/`).then((res) => {
        if (selectedChannel) {
          const sharedChannel = res.data.find((item: any) => item.channel_hash_id === selectedChannel.hash_id);
          const selectedSharedChannel = { ...selectedChannel };
          selectedSharedChannel['share'] = sharedChannel;
          selectedSharedChannel['playlists'] = sharedChannel?.playlists || [];
          handleSelectedChannel(selectedSharedChannel);
        }
        handleSharedChannels(res.data);
      });
    }
  }, []);

  const handleExternalSharing = async (value: boolean) => {
    setEnableExternal(value);
    await api.patch(`api/videos/${videoHashId}/sharing/`, { option: value ? 'enable' : 'disable' });
  };

  return (
    <div className="transparent font-lato">
      <div className="relative border bg-gray-bg-dark border-gray-300 rounded-lg bg-white pb-7 pt-9 px-7 w-screen md:w-[622px] md:min-h-[300px]">
        <div className="flex">
          <span className="font-bold text-2xl leading-6 text-[#1A1C1D]">External sharing</span>
          <CloseIcon className="ml-auto w-5 h-5 cursor-pointer opacity-70 hover:opacity-100" onClick={onCancel} />
        </div>
        <div className="flex justify-between mt-8 border-b py-4">
          <div className="flex">
            <LinkIcon className="mr-3 shrink-0 h-6 w-6 fill-black group-hover:fill-white" />
            <div>
              <p>External link</p>
              <p>Users can see the video after a registration page.</p>
              <div className="flex items-center mt-4">
                <label className="mr-6">{enableExternal ? 'On' : 'Off'}</label>
                <div className="mt-1">
                  <Switch
                    label="Switch"
                    type="green"
                    value={enableExternal}
                    onChange={(value: any) => handleExternalSharing(value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className={clsx(
              'w-36 h-11 rounded-[10px] text-base font-bold flex items-center justify-center ml-2 bg-green-600 cursor-pointer text-white'
            )}
            onClick={copyLink}
          >
            {copied ? 'Copied!' : 'Copy link'}
          </div>
        </div>
        <div className="flex justify-between border-b mt-8 pb-8">
          <div className="flex">
            <LinkIcon className="mr-3 shrink-0 h-6 w-6 fill-black group-hover:fill-white" />
            <div>
              <p>External link with SSO</p>
              {activeWorkspace?.sso_enabled ? (
                <p>Users must be authenticated to see the video.</p>
              ) : (
                <p className="flex">SSO must be enabled for the workspace to use this function.</p>
              )}
              <div className="h-8">
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={startAt}
                        onChange={() => {
                          setStartAt(!startAt);
                        }}
                      />
                    }
                    label={`Start at ${formattedTime}`}
                  />
                </FormGroup>
              </div>
            </div>
          </div>
          <div
            className={clsx(
              'w-36 h-11 rounded-[10px] text-base font-bold flex items-center justify-center ml-2cursor-pointer text-white',
              {
                'cursor-pointer': activeWorkspace?.sso_enabled,
                ' bg-gray-200': !activeWorkspace?.sso_enabled,
                ' bg-green-600': activeWorkspace?.sso_enabled
              }
            )}
            onClick={() => {
              if (activeWorkspace?.sso_enabled) {
                copySSOLink();
              }
            }}
          >
            {ssoCopied ? 'Copied!' : 'Copy link'}
          </div>
        </div>
        <div className="flex justify-between mt-8">
          <div className="flex">
            <LinkIcon className="mr-3 shrink-0 h-6 w-6 fill-black group-hover:fill-white" />
            <div>
              <p>Download link with SSO</p>
              {activeWorkspace?.sso_enabled ? (
                <p>Users must be authenticated to download this video.</p>
              ) : (
                <p className="flex">SSO must be enabled for the workspace to use this function.</p>
              )}
            </div>
          </div>
          <div
            className={clsx(
              'w-36 h-11 rounded-[10px] text-base font-bold flex items-center justify-center ml-2cursor-pointer text-white',
              {
                'cursor-pointer': activeWorkspace?.sso_enabled,
                ' bg-gray-200': !activeWorkspace?.sso_enabled,
                ' bg-green-600': activeWorkspace?.sso_enabled
              }
            )}
            onClick={() => {
              if (activeWorkspace?.sso_enabled) {
                copyDownloadSSOLink();
              }
            }}
          >
            {ssoDownloadCopied ? 'Copied!' : 'Copy link'}
          </div>
        </div>
        <div className="py-4 border-b"></div>
        <div className="mt-6 font-lato text-sm leading-[21px] font-bold">
          <p>{videoName}</p>
          {sharedChannels.filter((channel: any) => !channel.is_archived).length ? (
            <p className="text-[#8083A3] line-clamp-2">
              Shared in
              {sharedChannels
                .filter((channel: any) => !channel.is_archived)
                .map(
                  (channel: any) =>
                    channelList.filter((item: any) => item.hash_id === channel.channel_hash_id)?.[0]?.name
                )
                .map((item: string, idx: number) => (
                  <span className="text-[#1664A3]" key={uuid()}>
                    {idx !== 0 && ', '}
                    {' #'}
                    {item}
                  </span>
                ))}
            </p>
          ) : (
            <p className="text-[#8083A3]">Not shared in any channels</p>
          )}
        </div>
        <div className="flex items-center mt-5">
          <img
            className="rounded-sm cursor-pointer w-8 h-8 mr-2"
            src={selectedVideo?.owner?.avatar ? selectedVideo.owner.avatar : NoImg}
            alt="avatar"
          />
          <div className="text-sm text-[#1A1C1D]">
            <p className="font-bold">
              Owned by {selectedVideo?.owner.user_id === userData.user_id ? 'you' : selectedVideo?.owner?.name}
            </p>
            {/* <p className="font-normal text-[#8083A3]">Project Manager</p> */}
          </div>
        </div>
      </div>
    </div>
  );
}
